/**
 * INTENTIONAL MIGRATION: Switching from localStorage API key auth to GitHub OAuth with session cookies.
 *
 * This refactor is part of implementing the OAuth flow for the frontend analytics dashboard.
 * Authentication is now handled via secure HTTP-only cookies set by the backend OAuth callback.
 *
 * Changes:
 * - Removed login() function (users now authenticate via GitHub OAuth redirect)
 * - Added checkAuth() call on mount to verify session cookie validity
 * - Added user state to store authenticated user information
 * - Updated logout() to redirect to backend logout endpoint
 * - Added isAdmin field derived from user role
 * - Now fetches user info from /v1/me endpoint
 */

import { useState, useCallback, useEffect } from 'react'
import { apiClient } from '../api/client'

interface User {
  github_id: string
  username: string
  email?: string
  plan_tier: string
  role: string
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)

  // Check authentication status on mount by calling the backend API
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true)
      try {
        const isAuth = await apiClient.checkAuth()
        setIsAuthenticated(isAuth)

        // If authenticated, fetch user info from /v1/me endpoint
        if (isAuth) {
          const userData = await apiClient.get<User>('/v1/me')
          setUser(userData)
        }
      } catch (err) {
        setIsAuthenticated(false)
        setUser(null)
        // Don't set error on initial load - just silently fail
      } finally {
        setIsLoading(false)
      }
    }

    checkAuthStatus()
  }, [])

  const logout = useCallback(() => {
    // Redirect to backend logout endpoint which will clear the session cookie
    apiClient.logout()
  }, [])

  // Derive isAdmin from user role
  const isAdmin = user?.role === 'admin'

  return {
    isAuthenticated,
    isLoading,
    user,
    isAdmin,
    logout,
  }
}
